/**
 * BtybAuctionPage.jsx — a BTYB Marketer picks a city and places a CPM bid.
 * Shaped by: owner request for a rolling city CPM auction.
 * Invariants: one primary action; budget and CPM are entered here and stored by placeBtybBid;
 * other marketers' messages are not shown.
 * Related: btybAuctionService.js, BtybMarketerRoutes.jsx, Header.js.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { listBoundedCareStartCities } from "pages/pages/scmap4/careStartCityBounds";
import {
  centsToDollarString,
  callableErrorMessage,
  dollarsToCents,
  formatCents,
} from "components/dashboard/zipSlotAds/zipSlotAdsHelpers";
import {
  getBtybCityBoard,
  placeBtybBid,
} from "services/btybAuction/btybAuctionService";

function formatBudgetUnits(units) {
  const dollars = Number(units) / 100000;
  if (!Number.isFinite(dollars)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  }).format(dollars);
}

const BtybAuctionPage = () => {
  const cities = useMemo(() => listBoundedCareStartCities(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCity = searchParams.get("city") || "";
  const knownInitial = cities.some((city) => city.key === initialCity) ? initialCity : "";

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      cityKey: knownInitial,
      message: "",
      cpmDollars: "",
      budgetDollars: "",
    },
  });
  const cityKey = watch("cityKey");
  const selected = cities.find((city) => city.key === cityKey) || null;

  const [board, setBoard] = useState(null);
  const [loadingBoard, setLoadingBoard] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState("");

  const loadBoard = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    setSuccess("");
    setSubmitError("");
  }, [cityKey]);

  useEffect(() => {
    if (!cityKey) {
      setBoard(null);
      setLoadError(false);
      setLoadingBoard(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingBoard(true);
    setLoadError(false);
    getBtybCityBoard({ cityKey })
      .then((next) => {
        if (cancelled) return;
        setBoard(next);
        reset({
          cityKey,
          message: next?.yourBid?.message || "",
          cpmDollars: next?.yourBid ? centsToDollarString(next.yourBid.cpmCents) : "",
          budgetDollars: next?.yourBid
            ? centsToDollarString(next.yourBid.budgetCents)
            : "",
        });
      })
      .catch(() => {
        if (cancelled) return;
        setBoard(null);
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingBoard(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cityKey, reloadToken, reset]);

  const onSubmit = async (values) => {
    setSubmitError("");
    setSuccess("");
    const cpmCents = dollarsToCents(values.cpmDollars);
    const budgetCents = dollarsToCents(values.budgetDollars);
    if (cpmCents == null || budgetCents == null) {
      setSubmitError("Enter a CPM and a budget in dollars.");
      return;
    }
    setSubmitting(true);
    try {
      await placeBtybBid({
        cityKey: values.cityKey,
        message: values.message,
        cpmCents,
        budgetCents,
      });
      setSuccess("Bid saved. This city's page shows the highest CPM that can still pay for a view.");
      loadBoard();
    } catch (err) {
      setSubmitError(callableErrorMessage(err, "The bid could not be saved."));
    } finally {
      setSubmitting(false);
    }
  };

  const yourBid = board?.yourBid || null;
  const title = selected ? `Sponsor CareStart ${selected.cityName}` : "Sponsor a city";

  return (
    <Box>
      <DashboardPageHeader
        title={title}
        subtitle="One sponsor, named above the map."
      />
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <Typography color="text.secondary">
          Enter a CPM and a budget for one city. Each time that city&apos;s page
          is opened, the message from the highest CPM that can still pay for
          the view is shown. The next highest CPM is shown after the first
          budget can&apos;t cover another view.
        </Typography>

        <Controller
          name="cityKey"
          control={control}
          rules={{ required: "Select a city" }}
          render={({ field }) => (
            <Autocomplete
              options={cities}
              value={cities.find((city) => city.key === field.value) || null}
              onChange={(_, option) => {
                const nextKey = option?.key || "";
                field.onChange(nextKey);
                if (nextKey) setSearchParams({ city: nextKey });
                else setSearchParams({});
              }}
              getOptionLabel={(option) => `${option.cityName}, ${option.country}`}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  error={Boolean(errors.cityKey)}
                  helperText={errors.cityKey?.message || "The city this bid sponsors."}
                />
              )}
            />
          )}
        />

        {!cityKey ? (
          <Typography color="text.secondary">Select a city to place a bid.</Typography>
        ) : null}

        {loadingBoard ? <CircularProgress /> : null}

        {loadError ? (
          <DataLoadErrorAlert
            message="The standing for this city could not be loaded."
            onRetry={loadBoard}
          />
        ) : null}

        {cityKey && !loadingBoard && !loadError && yourBid?.serving ? (
          <Alert severity="success">
            Your message is the one on this city&apos;s page.
            {" "}
            {formatCents(yourBid.cpmCents)} CPM. Budget left{" "}
            {formatBudgetUnits(yourBid.budgetUnits)}. Page views served{" "}
            {yourBid.impressionsServed}.
          </Alert>
        ) : null}

        {cityKey && !loadingBoard && !loadError && yourBid && !yourBid.serving ? (
          <Alert severity="info">
            {yourBid.status === "exhausted"
              ? "Your budget for this city is used up. The next highest CPM is on the page."
              : `A higher CPM (${formatCents(board.leadingCpmCents)}) is on the page.`}
            {" "}
            Budget left {formatBudgetUnits(yourBid.budgetUnits)}. Page views served{" "}
            {yourBid.impressionsServed}.
          </Alert>
        ) : null}

        {cityKey && !loadingBoard && !loadError && !yourBid ? (
          <Typography color="text.secondary">This city has no sponsor yet.</Typography>
        ) : null}

        {success ? (
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
          </Alert>
        ) : null}
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}

        {cityKey && !loadingBoard && !loadError ? (
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Controller
                name="message"
                control={control}
                rules={{
                  required: "Enter the message for the banner",
                  maxLength: { value: 60, message: "Use 60 characters or fewer" },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Message"
                    error={Boolean(errors.message)}
                    helperText={errors.message?.message || "Shown inside the 320 by 50 banner."}
                  />
                )}
              />
              <Controller
                name="cpmDollars"
                control={control}
                rules={{
                  required: "Enter a CPM",
                  min: { value: 1, message: "CPM must be at least $1.00" },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="CPM (USD)"
                    type="number"
                    inputProps={{ min: 1, step: "0.01" }}
                    error={Boolean(errors.cpmDollars)}
                    helperText={errors.cpmDollars?.message || "Price per 1,000 page views."}
                  />
                )}
              />
              <Controller
                name="budgetDollars"
                control={control}
                rules={{
                  required: "Enter a budget",
                  min: { value: 1, message: "Budget must be at least $1.00" },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Budget (USD)"
                    type="number"
                    inputProps={{ min: 1, step: "0.01" }}
                    error={Boolean(errors.budgetDollars)}
                    helperText={
                      errors.budgetDollars?.message ||
                      "Spent when your message is shown. This screen does not charge a card."
                    }
                  />
                )}
              />
              <Box>
                <LoadingButton type="submit" variant="contained" loading={submitting}>
                  {yourBid ? "Update bid" : "Place a bid"}
                </LoadingButton>
              </Box>
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

export default BtybAuctionPage;
